import type { ReactNode, SVGProps } from 'react';
import { getEntityRegistryEntry, type EntityIconKey } from './entityRegistry';

type MarkProps = {
  size?: number;
  color?: string;
  className?: string;
} & Omit<SVGProps<SVGSVGElement>, 'children' | 'width' | 'height' | 'color'>;

/**
 * Organic silhouettes per archetype — soft Bézier blobs, still distinct at 12–20px.
 * No perfect circles/rects; each mark has its own “seed” outline.
 */
const SHAPE_PATH: Record<EntityIconKey, ReactNode> = {
  /* Product — soft diamond / seed */
  tag: (
    <path d="M12.2 1.6c1.1.4 3.8 2.6 6.4 5.4 2.1 2.3 3.2 4.4 2.9 6.3-.4 2.4-2.6 4.2-5.3 6.2-2.4 1.8-4.6 3.1-6.4 2.8-1.9-.3-3.6-2.2-5.4-5C2.6 14.1 1.4 11.6 1.7 9.4c.4-2.6 2.8-4.6 5.6-6.5C9.6 1.5 11.2 1.2 12.2 1.6Z" />
  ),
  /* Order — plump squircle pebble */
  'shopping-cart': (
    <path d="M7.2 2.4c3.2-1.2 7.1-.6 10.1 1.6 2.6 1.9 3.8 5 3.4 8.1-.4 3.4-2.6 6.4-5.8 7.8-3.1 1.4-6.8 1-9.4-1.5C2.8 15.8 1.6 12.4 2.2 9.1c.6-3.1 2.6-5.7 5-6.7Z" />
  ),
  /* Support — organic ring (hollow pebble) */
  lifebuoy: (
    <path
      fillRule="evenodd"
      d="M12.1 1.5c3.4-.3 6.8 1.4 8.6 4.4 1.9 3.1 1.6 7.1-.6 9.9-2.2 2.9-5.9 4.4-9.4 3.9-3.5-.5-6.5-3.1-7.4-6.5C2.4 9.6 3.8 5.9 6.7 3.8c2-1.5 4-2.1 5.4-2.3Zm.1 5.3c-2.6.2-4.7 2.1-5.1 4.5-.4 2.5 1.1 5 3.6 5.7 2.5.7 5.1-.7 5.9-3.1.8-2.5-.6-5.2-3.1-5.9-.4-.1-.9-.2-1.3-.2Z"
    />
  ),
  /* Staff — soft egg */
  'user-edit': (
    <path d="M12.4 2.1c2.8-.4 5.6 1.2 6.9 3.7 1.4 2.7.9 6-.9 8.4-1.8 2.3-4.7 3.5-7.3 3.1-2.7-.4-5-2.4-5.7-5-.8-2.8.4-5.9 2.8-7.5 1.7-1.2 3.1-1.5 4.2-1.7Z" />
  ),
  /* PO — tall bean */
  'clipboard-text': (
    <path d="M10.8 1.8c2.2-.6 4.6.2 5.9 2.1 1.2 1.7 1.1 3.9.6 6.4-.6 3.2-1.4 6.2-3.2 8.1-1.8 1.9-4.3 2.3-6.3 1.2-2-.1-3.4-2.4-3.8-5.2-.5-3.1.1-6.4 1.8-8.5C7.1 3.4 8.9 2.3 10.8 1.8Z" />
  ),
  /* Reception — teardrop pointing east */
  'truck-tick': (
    <path d="M3.4 7.2c1.2-3.1 4.4-5.2 7.8-5 3.2.2 6.3 2.1 8.1 4.8 1.4 2.1 1.2 4.4-.4 5.9-2.2 2.1-5.4 3.6-8.6 3.8-3.4.2-6.2-1.4-7.2-4.1-.9-2.3.1-4.4.3-5.4Z" />
  ),
  /* Client — horizontal kidney */
  people: (
    <path d="M3.2 9.1c1.4-3.2 5-5.4 9.1-5.2 3.8.2 7.2 2.4 8.2 5.4 1 2.9-.6 5.8-3.6 7.1-3.1 1.4-7.1 1.1-9.9-.9C4.2 13.7 2.4 11.2 3.2 9.1Z" />
  ),
  /* Shop — soft mound / hill */
  shop: (
    <path d="M12.1 2.2c2.1.3 4.6 2.4 6.6 4.9 1.6 2 2.6 4.1 2.1 5.9-.6 2.2-2.8 3.4-5.2 4.6-2.1 1-4.2 2.1-6.2 1.8-2.3-.4-4-2.1-5.2-4.2-1.3-2.3-1.4-4.8.2-6.6C6.4 5.9 9.1 3.4 12.1 2.2Z" />
  ),
  /* Quote — soft leaf / petal */
  'document-text': (
    <path d="M8.6 2.4c2.4-1.2 5.4-.4 7.4 1.8 1.9 2.1 2.6 5.1 2.1 8.1-.5 3.1-2.2 5.8-4.6 6.8-2.4 1-5 .1-6.6-2.2-1.7-2.4-2.1-5.6-1.2-8.2.7-2.1 1.8-3.6 2.9-4.3Z" />
  ),
  /* Fallback — river stone */
  box: (
    <path d="M11.6 2c2.9-.5 6.1 1 7.9 3.6 1.8 2.6 1.9 6.1.2 8.8-1.7 2.7-5 4.4-8.2 4.2-3.2-.2-6.1-2.3-7.2-5.2C3.1 10.4 4 7.1 6.3 4.9 8 3.4 9.8 2.3 11.6 2Z" />
  ),
};

const FALLBACK = SHAPE_PATH.box;

export function getEntityMarkShapeKey(entityType: string | null | undefined): EntityIconKey {
  return getEntityRegistryEntry(entityType)?.iconKey ?? 'box';
}

/** Unique solid mark for entity type (replaces Tabler icons in entity chrome). */
export function EntityMarkShape({
  entityType,
  size = 16,
  color = 'currentColor',
  className,
  ...rest
}: MarkProps & { entityType: string | null | undefined }): ReactNode {
  const key = getEntityMarkShapeKey(entityType);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
      focusable="false"
      {...rest}
    >
      {SHAPE_PATH[key] ?? FALLBACK}
    </svg>
  );
}
